import { client } from "@/hono";
import { useMentionOptions } from "@/hooks/use-mention-options";
import { useAssignmentReferenceBookingsModalContext } from "@/modals/partners/assignment-reference-bookings-modal/context";
import { BlankSlate } from "@msuite/ui/blank-slate";
import { ContentCard } from "@msuite/ui/content-card";
import { NumberInput, Textarea } from "@msuite/ui/form";
import { icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { useQuery } from "@msuite/ui/query";
import { cn, responseHandler } from "@msuite/ui/utils";
import { guardArray } from "@msuite/utils/array";
import type { AssignmentReferenceBookingsSchema } from "@msuite/utils/schemas";
import type { Nullable } from "@msuite/utils/types";
import { useFormContext } from "react-hook-form";
import { AssignmentReferenceBookingAnswerActions } from "./assignment-reference-booking-answer-actions";
import { AssignmentReferenceBookingPersonInput } from "./assignment-reference-booking-person-input";
import { AssignmentReferenceBookingRow } from "./assignment-reference-booking-row";
import { AssignmentReferenceBookingTimeInput } from "./assignment-reference-booking-time-input";
import {
	type BookingInputRole,
	bookingInputRoles,
	getBookingGridClassName,
	getBookingFieldPrefix,
	submittedColumnClassName,
} from "./booking-columns";
import { useBookingAnswer } from "./use-booking-answer";

/** Props Interface */
interface AssignmentReferenceSingleBookingFormProps {
	index: number;
	isLast: boolean;
	date: string;
}

const timeTypes = ["checkIn", "checkOut"] as const;

/**
 * What the host's column says where it shows no inputs: the booking's answer, or that it has none
 * yet. An approved record settles an unanswered booking as submitted, so there it reads accepted.
 */
function getAnswerSlate(result: Nullable<string>, isApproved: boolean) {
	if (result === "rejected") {
		return { iconName: "X", iconColor: "red", labelKey: "partners.rejected_booking" } as const;
	}
	if (result === "accepted" || isApproved) {
		return {
			iconName: "BadgeCheck",
			iconColor: "emerald",
			labelKey: "partners.accepted_booking",
		} as const;
	}
	return {
		iconName: "Hourglass",
		iconColor: "accent",
		labelKey: "partners.not_reviewed_yet",
	} as const;
}

export function AssignmentReferenceSingleBookingForm({
	index,
	date,
}: AssignmentReferenceSingleBookingFormProps) {
	/** Context */
	const methods = useFormContext<AssignmentReferenceBookingsSchema>();
	const { role, isApproved, hostCreatedBookingIds, hasHostColumn } =
		useAssignmentReferenceBookingsModalContext();

	/** Hooks */
	const { t } = useTranslation();
	const mentionOptions = useMentionOptions();
	const answer = useBookingAnswer(index);

	/** Functions */
	function handleOnRemoveBooking() {
		const bookings = methods.getValues("bookings");
		if (bookings.length <= 1) return;
		const newBookings = bookings.filter((_, i) => i !== index);
		methods.setValue("bookings", newBookings);
	}

	/** Constants */
	const userId = methods.getValues(`bookings.${index}.userId`);
	const bookingId = guardArray(methods.getValues("bookings"))[index]?.id;
	/**
	 * A row may be removed while the record is not approved and keeps at least one other row.
	 * The partner removes any of its rows; the host only the rows it added itself — on a record
	 * the partner left empty, where the generated first row counts as the host's — because a
	 * row the partner booked is answered through its adjustment, never deleted.
	 */
	const canRemoveBooking =
		!isApproved &&
		methods.getValues("bookings").length > 1 &&
		(role === "partner" || (!!bookingId && hostCreatedBookingIds.has(bookingId)));
	/**
	 * The person is the partner's statement: the partner names its people and the host answers
	 * hours through the adjustment, which has no counterpart for the person. So the host picks a
	 * person only on a row it added itself; on a row the partner booked, the name is read-only.
	 */
	const canChangePerson =
		!isApproved && (role === "partner" || (!!bookingId && hostCreatedBookingIds.has(bookingId)));

	/** Query */
	const { data } = useQuery({
		queryKey: ["user", userId],
		queryFn: async () => {
			if (!userId) return null;
			const response = await client.users[":userId"].$get({
				param: {
					userId,
				},
			});
			// The `users` policy shows a host a partner's person only while that person stands on
			// one of its boards, so a name the partner booked can be unreadable here. That is an
			// answer, not a failure: no toast, and the row is titled without a name.
			try {
				const data = await responseHandler(response, false);
				return data.user;
			} catch {
				return null;
			}
		},
		enabled: !!userId,
	});

	/** Computed */
	const fullName = data?.name ?? "";
	const title = fullName
		? `${index + 1}. ${fullName}`
		: `${index + 1}. ${t("personnel.employees.employee")}`;
	// The host's column shows inputs while the host corrects the booking and the correction once
	// it is given; every other answer, and the lack of one, is a slate naming it.
	const showsHostInputs = answer.isEditing || answer.result === "adjusted";
	// The partner writes its side until the record is approved; the host writes its own only
	// while a correction is open.
	const isDisabled = {
		partner: role !== "partner" || isApproved,
		host: !answer.isEditing,
	};
	const canAnswer = role === "host" && !isApproved;
	const inputRoles: readonly BookingInputRole[] =
		hasHostColumn && showsHostInputs ? bookingInputRoles : ["partner"];
	const slate = getAnswerSlate(answer.result, isApproved);

	/** Render */
	return (
		<ContentCard
			variant="plain"
			title={title}
			id={`booking-${bookingId}`}
			icon={icon("User")}
			className="mx-5"
			menuItems={[
				"divider",
				// Only where the row may be removed. A partner's row in the host's view, the last
				// row and an approved record offer no entry at all: the host's refusal of a row is
				// the reject answer, not a greyed-out delete.
				...(canRemoveBooking
					? [
							{
								label: t("partners.remove_booking"),
								onClick: handleOnRemoveBooking,
								icon: icon("Trash2"),
								isDanger: true,
								closesMenu: true,
							},
						]
					: []),
			]}
		>
			<div className={cn(getBookingGridClassName(hasHostColumn), "items-start gap-y-2")}>
				{/* The person is the partner's statement alone, so it stands in the partner's column; the
				host's answers take the line's other cell. */}
				<AssignmentReferenceBookingRow label={t("personnel.employees.employee")}>
					<AssignmentReferenceBookingPersonInput
						index={index}
						canChangePerson={canChangePerson}
						fullName={fullName}
					/>
					{canAnswer && (
						<div className="self-center">
							<AssignmentReferenceBookingAnswerActions
								isEditing={answer.isEditing}
								onAccept={answer.accept}
								onReject={answer.reject}
								onEdit={answer.startEditing}
								onCancel={answer.cancelEditing}
								onConfirm={answer.confirmEditing}
							/>
						</div>
					)}
				</AssignmentReferenceBookingRow>
				{timeTypes.map((timeType) => (
					<AssignmentReferenceBookingRow
						key={timeType}
						label={timeType === "checkIn" ? t("partners.start") : t("partners.end")}
					>
						{inputRoles.map((inputRole) => (
							<AssignmentReferenceBookingTimeInput
								key={inputRole}
								index={index}
								inputRole={inputRole}
								timeType={timeType}
								date={date}
								isDisabled={isDisabled[inputRole]}
							/>
						))}
					</AssignmentReferenceBookingRow>
				))}
				<AssignmentReferenceBookingRow label={t("partners.pause")}>
					{inputRoles.map((inputRole) => (
						<NumberInput
							key={inputRole}
							min={0}
							max={24}
							step={0.25}
							control={methods.control}
							name={`bookings.${index}.${getBookingFieldPrefix(inputRole)}pause`}
							label={undefined}
							placeholder="0,00"
							isRequired
							isDisabled={isDisabled[inputRole]}
							variant="hour"
							className={inputRole === "partner" ? submittedColumnClassName : undefined}
						/>
					))}
				</AssignmentReferenceBookingRow>
				<AssignmentReferenceBookingRow label={t("notes.note")}>
					{inputRoles.map((inputRole) => (
						<Textarea
							key={inputRole}
							control={methods.control}
							name={`bookings.${index}.${getBookingFieldPrefix(inputRole)}note`}
							label={undefined}
							placeholder={t("notes.add_note")}
							isRequired={false}
							isDisabled={isDisabled[inputRole]}
							disableFormatting
							minHeight={0}
							className={inputRole === "partner" ? submittedColumnClassName : undefined}
							{...mentionOptions}
						/>
					))}
				</AssignmentReferenceBookingRow>
				{hasHostColumn && !showsHostInputs && (
					// One slate beside the booking: across the four lines the host answers, and up
					// beside the person too where no answers stand there.
					<BlankSlate
						className={cn(
							"col-start-3 self-stretch",
							canAnswer ? "row-span-4 row-start-2" : "row-span-5 row-start-1",
						)}
						withBorder
						Icon={icon(slate.iconName)}
						iconColor={slate.iconColor}
						label={t(slate.labelKey)}
					/>
				)}
			</div>
		</ContentCard>
	);
}
